/**
 * Vendors - spec 09's partner directory (FR-6.1.x).
 *
 * FR-8.6 is the reason every mutation here matters: `publish` makes a partner
 * visible to members on their next request. Nothing is shipped in the Expo
 * binary, so an admin adding a partner, changing a discount or hiding one takes
 * effect immediately - and the "Visibility" column is the honest statement of
 * what members can currently see, since the member-visible predicate is
 * `status = 'published' AND deleted_at IS NULL` plus the `valid_from`/`valid_to`
 * window.
 *
 * Soft delete only (FR-6.1.5): Archive and Delete both leave the row, so the
 * partner's contact history survives. The difference is intent - `unpublish`
 * keeps it editable and re-publishable, `archive` marks it retired, and Delete
 * sets `deleted_at` and takes it out of every list here by default.
 *
 * Categories live on their own screen (`CategoriesScreen`), because the same
 * table files FamilyBiz listings too; the partner form only picks from it.
 */

import { useRef, useState } from "react";

import { DataTable, PrimaryCell, type Column } from "../components/DataTable";
import {
  Avatar,
  Badge,
  Button,
  Card,
  ErrorNotice,
  EmptyState,
  Field,
  Input,
  LoadingRows,
  Modal,
  Page,
  Pagination,
  Select,
  SuccessNotice,
  Textarea,
  statusTone,
} from "../components/primitives";
import {
  archiveVendor,
  createVendor,
  deleteVendor,
  fetchCategories,
  fetchVendors,
  publishVendor,
  unpublishVendor,
  removeVendorPhoto,
  updateVendor,
  uploadVendorLogo,
  uploadVendorPhoto,
  type Category,
  type Vendor,
  type VendorPhoto,
  type VendorWrite,
} from "../lib/api/vendors";
import type { DiscountType, Page as PageType, VendorStatus } from "../lib/api/types";
import { confirmAction, useMutation, useQuery } from "../lib/hooks";
import { formatDate, orDash } from "../lib/format";
import { formatBytes, optimizePhoto } from "../lib/image";

const PAGE_SIZE = 50;
const STATUSES: VendorStatus[] = ["draft", "published", "unpublished", "archived"];
const DISCOUNT_TYPES: DiscountType[] = ["percentage", "amount", "other"];

export function VendorsScreen() {
  const [q, setQ] = useState("");
  const [committedQ, setCommittedQ] = useState("");
  const [status, setStatus] = useState<VendorStatus | "">("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<{ vendor: Vendor | null } | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const vendorsQuery = useQuery<PageType<Vendor>>(
    () =>
      fetchVendors({
        q: committedQ || undefined,
        status: status || undefined,
        include_deleted: includeDeleted || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    [committedQ, status, includeDeleted, offset],
  );
  const { data, error, loading, refreshing, reload } = vendorsQuery;

  const action = useMutation(
    async (input: { kind: string; vendor: Vendor }) => {
      const { kind, vendor } = input;
      if (kind === "publish") return publishVendor(vendor.id);
      if (kind === "unpublish") return unpublishVendor(vendor.id);
      if (kind === "archive") return archiveVendor(vendor.id);
      return deleteVendor(vendor.id);
    },
    {
      onSuccess: (result) => {
        setSaved(result?.message ?? "Done.");
        reload();
      },
    },
  );

  const columns: Array<Column<Vendor>> = [
    {
      key: "name",
      header: "Partner",
      render: (row) => (
        <div className="row row--tight">
          <Avatar name={row.name} url={row.logo_url} />
          <PrimaryCell
            title={
              <>
                {row.name}
                {row.is_featured ? (
                  <>
                    {" "}
                    <Badge tone="pending">featured</Badge>
                  </>
                ) : null}
              </>
            }
            detail={row.category_name ?? "Uncategorised"}
          />
        </div>
      ),
    },
    {
      key: "discount",
      header: "Offer",
      render: (row) =>
        row.discount_label ? (
          <PrimaryCell title={row.discount_label} detail={row.discount_type ?? undefined} />
        ) : (
          <span className="text-meta">No discount</span>
        ),
    },
    {
      key: "contact",
      header: "Contact",
      render: (row) => (
        <PrimaryCell
          title={orDash(row.phone ?? row.email)}
          detail={row.phone && row.email ? row.email : undefined}
        />
      ),
    },
    {
      key: "validity",
      header: "Valid",
      render: (row) =>
        row.valid_from || row.valid_to ? (
          <PrimaryCell
            title={`${row.valid_from ? formatDate(row.valid_from) : "open"} → ${row.valid_to ? formatDate(row.valid_to) : "open"}`}
          />
        ) : (
          <span className="text-meta">Always</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <>
          <Badge tone={statusTone(row.status)}>{row.status}</Badge>
          {row.deleted_at ? (
            <>
              {" "}
              <Badge tone="danger">deleted</Badge>
            </>
          ) : null}
        </>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <div className="table__actions">
          <Button size="sm" onClick={() => setEditing({ vendor: row })}>
            Edit
          </Button>
          {row.status === "published" ? (
            <Button size="sm" onClick={() => void action.run({ kind: "unpublish", vendor: row })}>
              Unpublish
            </Button>
          ) : (
            <Button
              size="sm"
              variant="accent"
              disabled={Boolean(row.deleted_at)}
              title={row.deleted_at ? "Restore is not supported; create a new partner instead." : undefined}
              onClick={() => void action.run({ kind: "publish", vendor: row })}
            >
              Publish
            </Button>
          )}
          <Button
            size="sm"
            variant="danger"
            onClick={() => {
              if (confirmAction(`Archive "${row.name}"? Members stop seeing it, and the record is kept.`)) {
                void action.run({ kind: "archive", vendor: row });
              }
            }}
          >
            Archive
          </Button>
          {!row.deleted_at ? (
            <Button
              size="sm"
              variant="quiet"
              onClick={() => {
                if (
                  confirmAction(
                    `Delete "${row.name}"? It disappears from every list. Its contact history is kept, and this cannot be undone from here.`,
                  )
                ) {
                  void action.run({ kind: "delete", vendor: row });
                }
              }}
            >
              Delete
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <Page>
      {saved ? <SuccessNotice message={saved} onDismiss={() => setSaved(null)} /> : null}
      {action.error ? <ErrorNotice error={action.error} /> : null}

      <Card>
        <form
          className="filters"
          onSubmit={(event) => {
            event.preventDefault();
            setCommittedQ(q.trim());
            setOffset(0);
          }}
        >
          <div style={{ width: 260 }}>
            <Field label="Search">
              {(id) => (
                <Input
                  id={id}
                  value={q}
                  onChange={(event) => setQ(event.target.value)}
                  placeholder="Partner name"
                  maxLength={120}
                />
              )}
            </Field>
          </div>

          <div style={{ width: 160 }}>
            <Field label="Status">
              {(id) => (
                <Select
                  id={id}
                  value={status}
                  onChange={(event) => {
                    setStatus(event.target.value as VendorStatus | "");
                    setOffset(0);
                  }}
                >
                  <option value="">All</option>
                  {STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <label className="row row--tight" style={{ gap: 6, paddingBottom: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(event) => {
                setIncludeDeleted(event.target.checked);
                setOffset(0);
              }}
            />
            <span className="text-small">Include deleted</span>
          </label>

          <div className="row row--tight">
            <Button type="submit" variant="primary">
              Search
            </Button>
            <Button
              onClick={() => {
                setQ("");
                setCommittedQ("");
                setStatus("");
                setIncludeDeleted(false);
                setOffset(0);
              }}
            >
              Clear
            </Button>
            <Button onClick={reload} disabled={refreshing}>
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
            <Button variant="accent" onClick={() => setEditing({ vendor: null })}>
              New partner
            </Button>
          </div>
        </form>
      </Card>

      {error ? <ErrorNotice error={error} onRetry={reload} /> : null}

      <Card flush>
        {loading && !data ? (
          <LoadingRows rows={6} columns={6} />
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            title="No partners match these filters."
            body="Partners appear in the member app as soon as they are published."
            action={
              <Button size="sm" variant="accent" onClick={() => setEditing({ vendor: null })}>
                New partner
              </Button>
            }
          />
        ) : (
          <>
            <DataTable columns={columns} rows={data.items} rowKey={(row) => row.id} />
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              noun="partners"
              onChange={setOffset}
            />
          </>
        )}
      </Card>

      {editing ? (
        <VendorFormDialog
          vendor={editing.vendor}
          onClose={() => setEditing(null)}
          onLogoSaved={reload}
          onDone={(message) => {
            setEditing(null);
            setSaved(message);
            reload();
          }}
        />
      ) : null}
    </Page>
  );
}

/** Prefilled in the Website field so the admin only types the domain. */
const WEBSITE_PREFIX = "https://";

function VendorFormDialog({
  vendor,
  onClose,
  onLogoSaved,
  onDone,
}: {
  vendor: Vendor | null;
  onClose: () => void;
  /** The logo is saved on its own request, so the list behind the dialog is reloaded then too. */
  onLogoSaved: () => void;
  onDone: (message: string) => void;
}) {
  const isNew = vendor === null;
  const [form, setForm] = useState({
    name: vendor?.name ?? "",
    slug: vendor?.slug ?? "",
    short_description: vendor?.short_description ?? "",
    description: vendor?.description ?? "",
    category_id: vendor?.category_id ?? "",
    phone: vendor?.phone ?? "",
    email: vendor?.email ?? "",
    website_url: vendor?.website_url || WEBSITE_PREFIX,
    address: vendor?.address ?? "",
    map_url: vendor?.map_url ?? "",
    discount_type: (vendor?.discount_type ?? "") as DiscountType | "",
    discount_value: vendor?.discount_value ?? "",
    discount_terms: vendor?.discount_terms ?? "",
    valid_from: vendor?.valid_from ?? "",
    valid_to: vendor?.valid_to ?? "",
    is_featured: vendor?.is_featured ?? false,
    display_order: String(vendor?.display_order ?? 0),
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(vendor?.logo_url ?? null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const [logoNotice, setLogoNotice] = useState<string | null>(null);
  /** Set once the row exists - on edit immediately, on create after the first save. */
  const [savedId, setSavedId] = useState<string | null>(vendor?.id ?? null);

  const categories = useQuery<{ items: Category[]; total: number }>(
    () => fetchCategories(),
    [],
  );

  function payload(): VendorWrite {
    const body: VendorWrite = { name: form.name.trim() };
    if (form.slug.trim()) body.slug = form.slug.trim();
    if (form.short_description.trim()) body.short_description = form.short_description.trim();
    if (form.description.trim()) body.description = form.description.trim();
    if (form.category_id) body.category_id = form.category_id;
    if (form.phone.trim()) body.phone = form.phone.trim();
    if (form.email.trim()) body.email = form.email.trim();
    const website = form.website_url.trim();
    if (website && website !== WEBSITE_PREFIX) body.website_url = website;
    if (form.address.trim()) body.address = form.address.trim();
    if (form.map_url.trim()) body.map_url = form.map_url.trim();
    if (form.discount_type) body.discount_type = form.discount_type;
    if (form.discount_value.trim()) body.discount_value = form.discount_value.trim();
    if (form.discount_terms.trim()) body.discount_terms = form.discount_terms.trim();
    if (form.valid_from) body.valid_from = form.valid_from;
    if (form.valid_to) body.valid_to = form.valid_to;
    body.is_featured = form.is_featured;
    body.display_order = Number(form.display_order) || 0;
    return body;
  }

  /**
   * Save the JSON fields. The logo is a SEPARATE request and never bundled in:
   * it is multipart with its own endpoint and its own audit action
   * (`vendor.logo`), and the API's `logo_storage_unavailable` promise is
   * explicitly that "the other partner details are saved" - which only holds if
   * they were saved by a different call.
   */
  const save = useMutation(
    () => (savedId ? updateVendor(savedId, payload()) : createVendor(payload())),
    {
      onSuccess: (result) => {
        const id = result?.vendor.id ?? null;
        if (id) setSavedId(id);
        if (vendor) {
          // An edit: the admin came here to change one thing, so close.
          onDone(result?.message ?? "Partner saved.");
          return;
        }
        // A create: keep the dialog open so a logo can be attached to the row that
        // now exists. Closing would mean reopening to upload, and the file the
        // admin already chose would be gone.
        setSavedNotice(
          `${result?.message ?? "Partner saved."} Attach a logo now, or close - it can be added later.`,
        );
      },
    },
  );

  const uploadLogo = useMutation((file: File) => uploadVendorLogo(savedId as string, file), {
    onSuccess: (result) => {
      setLogoFile(null);
      setLogoUrl(result.logo_url);
      setLogoNotice(result.message);
      onLogoSaved();
    },
  });

  return (
    <Modal
      title={isNew ? "New partner" : `Edit ${vendor.name}`}
      description="Members see a partner only when it is published and inside its validity window."
      onClose={onClose}
      wide
    >
      <div className="stack" style={{ gap: 16 }}>
        <div className="form-grid">
          <Field label="Name (required)">
            {(id) => (
              <Input
                id={id}
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                maxLength={160}
              />
            )}
          </Field>
          <Field label="Link name" hint="Optional, used in URLs.">
            {(id) => (
              <Input
                id={id}
                value={form.slug}
                onChange={(event) => setForm({ ...form, slug: event.target.value })}
                maxLength={160}
              />
            )}
          </Field>
          <Field label="Category">
            {(id) => (
              <Select
                id={id}
                value={form.category_id}
                onChange={(event) => setForm({ ...form, category_id: event.target.value })}
              >
                <option value="">Uncategorised</option>
                {(categories.data?.items ?? []).map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Sort order" hint="Lower numbers appear first.">
            {(id) => (
              <Input
                id={id}
                type="number"
                value={form.display_order}
                onChange={(event) => setForm({ ...form, display_order: event.target.value })}
              />
            )}
          </Field>
          <Field label="Phone">
            {(id) => (
              <Input id={id} value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
            )}
          </Field>
          <Field label="Email">
            {(id) => (
              <Input
                id={id}
                type="email"
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
              />
            )}
          </Field>
          <Field label="Website">
            {(id) => (
              <Input
                id={id}
                value={form.website_url}
                onChange={(event) =>
                  // A full address pasted after the prefilled scheme keeps only one scheme.
                  setForm({ ...form, website_url: event.target.value.replace(/^https?:\/\/(?=https?:\/\/)/i, "") })
                }
                placeholder="https://"
              />
            )}
          </Field>
          <Field label="Map link">
            {(id) => (
              <Input
                id={id}
                value={form.map_url}
                onChange={(event) => setForm({ ...form, map_url: event.target.value })}
                placeholder="https://maps.google.com/…"
              />
            )}
          </Field>
          <div className="form-grid__wide">
            <Field label="Address">
              {(id) => (
                <Input id={id} value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} />
              )}
            </Field>
          </div>
          <div className="form-grid__wide">
            <Field label="Short description" hint="One line, shown on the directory tile.">
              {(id) => (
                <Input
                  id={id}
                  value={form.short_description}
                  onChange={(event) => setForm({ ...form, short_description: event.target.value })}
                  maxLength={200}
                />
              )}
            </Field>
          </div>
          <div className="form-grid__wide">
            <Field label="Description">
              {(id) => (
                <Textarea
                  id={id}
                  value={form.description}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                />
              )}
            </Field>
          </div>
          <Field label="Discount type">
            {(id) => (
              <Select
                id={id}
                value={form.discount_type}
                onChange={(event) => setForm({ ...form, discount_type: event.target.value as DiscountType | "" })}
              >
                <option value="">No discount</option>
                {DISCOUNT_TYPES.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Discount value" hint="A number: 5 for 5%, or an amount in NPR.">
            {(id) => (
              <Input
                id={id}
                inputMode="decimal"
                value={form.discount_value}
                onChange={(event) => setForm({ ...form, discount_value: event.target.value })}
              />
            )}
          </Field>
          <Field label="Valid from">
            {(id) => (
              <Input
                id={id}
                type="date"
                value={form.valid_from}
                onChange={(event) => setForm({ ...form, valid_from: event.target.value })}
              />
            )}
          </Field>
          <Field label="Valid to">
            {(id) => (
              <Input
                id={id}
                type="date"
                value={form.valid_to}
                onChange={(event) => setForm({ ...form, valid_to: event.target.value })}
              />
            )}
          </Field>
          <div className="form-grid__wide">
            <Field label="Discount terms">
              {(id) => (
                <Textarea
                  id={id}
                  value={form.discount_terms}
                  onChange={(event) => setForm({ ...form, discount_terms: event.target.value })}
                />
              )}
            </Field>
          </div>
          <div className="form-grid__wide">
            <Field
              label="Logo"
              hint={
                savedId
                  ? `${logoUrl ? "" : "No logo yet. "}JPEG, PNG or WebP up to 2 MB. Checked from the file's own bytes, not its name.`
                  : "Save the partner first, then attach a logo."
              }
            >
              {(id) => (
                <div className="row row--tight">
                  <Avatar name={form.name || "?"} url={logoUrl} />
                  <input
                    id={id}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={!savedId}
                    onChange={(event) => setLogoFile(event.target.files?.[0] ?? null)}
                  />
                  <Button
                    size="sm"
                    disabled={!savedId || !logoFile || uploadLogo.pending}
                    onClick={() => {
                      if (logoFile) void uploadLogo.run(logoFile);
                    }}
                  >
                    {uploadLogo.pending ? "Uploading…" : "Upload logo"}
                  </Button>
                </div>
              )}
            </Field>
          </div>
          <div className="form-grid__wide">
            <VendorPhotosField vendorId={savedId} initial={vendor?.photos ?? []} onSaved={onLogoSaved} />
          </div>
          <label className="row row--tight" style={{ gap: 6, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={form.is_featured}
              onChange={(event) => setForm({ ...form, is_featured: event.target.checked })}
            />
            <span className="text-small">Featured on Home</span>
          </label>
        </div>

        <ErrorNotice error={save.error} />
        <ErrorNotice error={uploadLogo.error} />
        {savedNotice ? <SuccessNotice message={savedNotice} onDismiss={() => setSavedNotice(null)} /> : null}
        {logoNotice ? <SuccessNotice message={logoNotice} onDismiss={() => setLogoNotice(null)} /> : null}

        <div className="modal__actions">
          <Button onClick={onClose} disabled={save.pending || uploadLogo.pending}>
            {savedId && !vendor ? "Close" : "Cancel"}
          </Button>
          <Button
            variant="accent"
            disabled={!form.name.trim() || save.pending || uploadLogo.pending}
            onClick={() => void save.run()}
          >
            {save.pending ? "Saving…" : savedId && !vendor ? "Save changes" : "Save partner"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const PHOTO_SLOTS = [0, 1, 2];

type PhotoBusy = { slot: number; stage: "Optimizing…" | "Uploading…" | "Removing…" };

/**
 * Spec 21 - up to three photos, shown to members as the swipeable banner on the
 * partner's page. Like the logo, each change is its own request and is live as
 * soon as it succeeds; the Save button above does not touch photos.
 *
 * A picked file is cropped to 4:3, resized and re-encoded in the browser
 * (`optimizePhoto`) before it is sent, so the tile shows exactly the frame
 * members will see and the upload is a few hundred KB rather than a raw
 * phone photo. One change at a time: the response carries every slot, and
 * letting two race would let the older answer redraw over the newer one.
 */
function VendorPhotosField({
  vendorId,
  initial,
  onSaved,
}: {
  vendorId: string | null;
  initial: VendorPhoto[];
  onSaved: () => void;
}) {
  const [photos, setPhotos] = useState<VendorPhoto[]>(initial);
  const [busy, setBusy] = useState<PhotoBusy | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const targetSlot = useRef(0);

  function pick(slot: number) {
    targetSlot.current = slot;
    input.current?.click();
  }

  async function upload(file: File) {
    if (!vendorId) return;
    const slot = targetSlot.current;
    setError(null);
    setNotice(null);
    try {
      setBusy({ slot, stage: "Optimizing…" });
      const optimized = await optimizePhoto(file);
      setBusy({ slot, stage: "Uploading…" });
      const result = await uploadVendorPhoto(vendorId, slot, optimized.blob, optimized.fileName);
      setPhotos(result.photos);
      setNotice(
        `${result.message} Optimised from ${formatBytes(optimized.before)} to ${formatBytes(optimized.after)} ` +
          `(${optimized.width}×${optimized.height}).`,
      );
      onSaved();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(null);
    }
  }

  async function remove(slot: number) {
    if (!vendorId || !confirmAction("Remove this photo? Members stop seeing it straight away.")) return;
    setError(null);
    setNotice(null);
    try {
      setBusy({ slot, stage: "Removing…" });
      const result = await removeVendorPhoto(vendorId, slot);
      setPhotos(result.photos);
      setNotice(result.message);
      onSaved();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Field
      label="Photos"
      hint={
        vendorId
          ? "Up to 3, shown as a swipeable banner on the partner's page. Each is cropped to 4:3, resized to 1600px and compressed before upload."
          : "Save the partner first, then add photos."
      }
    >
      {(id) => (
        <div id={id} className="stack" style={{ gap: 10 }} role="group" aria-label="Partner photos">
          <input
            ref={input}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Cleared so choosing the same file again still fires `change`.
              event.target.value = "";
              if (file) void upload(file);
            }}
          />
          <div className="photo-slots">
            {PHOTO_SLOTS.map((slot) => {
              const photo = photos.find((item) => item.slot === slot);
              const working = busy?.slot === slot ? busy.stage : null;
              return (
                <div key={slot} className="photo-slot">
                  <div className="photo-slot__frame">
                    {photo ? (
                      <img src={photo.url} alt={`Photo ${slot + 1}`} loading="lazy" />
                    ) : (
                      <span className="photo-slot__empty">Photo {slot + 1}</span>
                    )}
                    {working ? <span className="photo-slot__busy">{working}</span> : null}
                  </div>
                  <div className="row row--tight">
                    <Button size="sm" disabled={!vendorId || busy !== null} onClick={() => pick(slot)}>
                      {photo ? "Replace" : "Add photo"}
                    </Button>
                    {photo ? (
                      <Button
                        size="sm"
                        variant="quiet"
                        disabled={busy !== null}
                        onClick={() => void remove(slot)}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          <ErrorNotice error={error} />
          {notice ? <SuccessNotice message={notice} onDismiss={() => setNotice(null)} /> : null}
        </div>
      )}
    </Field>
  );
}
